import { Button } from '@/modules/shared/components/ui/button'
import { Card, CardContent } from '@/modules/shared/components/ui/card'
import { Checkbox } from '@/modules/shared/components/ui/checkbox'
import { Input } from '@/modules/shared/components/ui/input'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/modules/shared/components/ui/popover'
import { useState } from 'react'
import type { ComparativeSeller } from '../../types/comparative-report.type'

interface Props {
  sellers: ComparativeSeller[]
  selectedIds: number[]
  onChange: (ids: number[] | null) => void
}
const normalize = (value: string) =>
  value.normalize('NFD').replace(/\p{M}/gu, '').toLocaleLowerCase('pt-BR')

export function ComparativeSellerSelector({
  sellers,
  selectedIds,
  onChange,
}: Props) {
  const [search, setSearch] = useState('')
  const selected = new Set(selectedIds)
  const visible = sellers.filter((seller) =>
    normalize(seller.nomeVendedor).includes(normalize(search)),
  )
  const allSelected =
    sellers.length > 0 && selectedIds.length === sellers.length

  function toggle(id: number) {
    const next = selected.has(id)
      ? selectedIds.filter((value) => value !== id)
      : [...selectedIds, id]
    onChange(next.length === sellers.length ? null : next)
  }

  return (
    <Card>
      <CardContent className="space-y-2 p-4">
        <label htmlFor="comparative-sellers" className="text-sm font-medium">
          Vendedores para comparação
        </label>
        <Popover>
          <PopoverTrigger asChild>
            <Button
              id="comparative-sellers"
              variant="outline"
              className="w-full justify-between font-normal"
            >
              <span>
                {allSelected
                  ? 'Todos os vendedores'
                  : `${selectedIds.length} vendedores selecionados`}
              </span>
              <span className="text-muted-foreground">
                {selectedIds.length}/{sellers.length}
              </span>
            </Button>
          </PopoverTrigger>
          <PopoverContent
            align="start"
            className="w-[var(--radix-popover-trigger-width)] p-3"
          >
            <Input
              autoFocus
              placeholder="Buscar vendedor pelo nome..."
              aria-label="Buscar vendedor pelo nome"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
            <div className="flex flex-wrap gap-2 py-2">
              <Button variant="ghost" size="sm" onClick={() => onChange(null)}>
                Selecionar todos
              </Button>
              <Button variant="ghost" size="sm" onClick={() => onChange([])}>
                Limpar seleção
              </Button>
            </div>
            <div className="max-h-64 space-y-1 overflow-y-auto">
              {visible.length === 0 && (
                <p className="p-2 text-sm text-muted-foreground">
                  Nenhum vendedor encontrado.
                </p>
              )}
              {visible.map((seller) => (
                <label
                  key={seller.fkVendedor}
                  className="flex cursor-pointer items-start gap-3 rounded p-2 text-sm hover:bg-muted"
                >
                  <Checkbox
                    className="mt-0.5 shrink-0"
                    checked={selected.has(seller.fkVendedor)}
                    onCheckedChange={() => toggle(seller.fkVendedor)}
                  />
                  <span>{seller.nomeVendedor}</span>
                </label>
              ))}
            </div>
          </PopoverContent>
        </Popover>
      </CardContent>
    </Card>
  )
}
